import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaHistory, FaChevronLeft, FaChevronRight, FaPenAlt } from 'react-icons/fa';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const TestHistoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [testHistory, setTestHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');

  // Process test history data to handle writing scores properly
  const processTestHistory = (tests) => {
    return tests.map(test => {
      // Handle writing tests specifically
      if (test.subject === 'writing' && test.testType === 'mock') {
        return {
          ...test,
          // For writing tests, use teacher_score if available, otherwise use score
          scorePercentage: test.teacher_score !== null && test.teacher_score !== undefined
            ? test.teacher_score * 10  // Convert 0-10 to 0-100
            : test.score !== null && test.score !== undefined
              ? test.score * 10  // Convert 0-10 to 0-100
              : 0,
          // Ensure totalQuestions is set for writing tests
          totalQuestions: test.totalQuestions || 1,
          // Ensure correctAnswers is set for writing tests
          correctAnswers: test.teacher_score !== null && test.teacher_score !== undefined
            ? test.teacher_score
            : test.score || 0
        };
      }

      // For other tests, ensure scorePercentage is calculated properly
      return {
        ...test,
        scorePercentage: test.scorePercentage !== undefined
          ? test.scorePercentage
          : test.totalQuestions > 0
            ? Math.round((test.correctAnswers / test.totalQuestions) * 100)
            : 0
      };
    });
  };

  useEffect(() => {
    const filter = location.state?.filter || 'all';
    setFilterType(filter);

    const fetchTestHistory = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/student/test-history/${user.id}`
          );

          // Process the test history data
          const processedHistory = processTestHistory(response.data);
          setTestHistory(processedHistory);

          // Apply initial filter
          const filtered = filterTests(processedHistory, filter);
          setFilteredHistory(filtered);

          setLoading(false);
        } catch (error) {
          console.error("Error fetching test history:", error);
          setError("Failed to load test history. Please try again.");
          setLoading(false);
        }
      }
    };

    fetchTestHistory();
  }, [location.state]);

  // Strict filter function - properly separate test types
  const filterTests = (tests, filter) => {
    switch (filter) {
      case 'mock':
        return tests.filter(test =>
          test.testType && test.testType.toLowerCase() === 'mock'
        );
      case 'premium':
        return tests.filter(test =>
          ['practice', 'weekly', 'mega'].includes(test.testType)
        );
      case 'subjects':
        return tests.filter(test =>
          !['practice', 'weekly', 'mega', 'mock'].includes(test.testType)
        );
      default:
        return tests;
    }
  };

  const getTestTypeName = (testType, category, subject) => {
    const formattedSubject = formatSubjectName(subject);

    switch (testType) {
      case 'practice':
        return 'Practice Test';

      case 'weekly':
        return 'Weekly Test';

      case 'mega':
        return 'Mega Test';

      case 'mock':
        return formattedSubject
          ? `${formattedSubject} Mock Test`
          : 'Mock Test';

      case 'badge':
        return `${category} Badge Challenge`;

      default:
        return category
          ? `${category} Test`
          : 'Topic Test';
    }
  };


  const formatTestDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get score display for writing tests
  const getScoreDisplay = (test) => {
    if (test.subject === 'writing' && test.testType === 'mock') {
      const teacherScore = test.teacher_score !== null && test.teacher_score !== undefined
        ? test.teacher_score
        : null;
      const aiScore = test.score !== null && test.score !== undefined ? test.score : null;

      if (teacherScore !== null) {
        return `${teacherScore}/10`;
      } else if (aiScore !== null) {
        return `${aiScore}/10`;
      }
      return 'Pending';
    }

    // For MCQ tests, show percentage
    return `${test.scorePercentage}%`;
  };

  // Get score color based on test type and score
  const getScoreColor = (test) => {
    if (test.subject === 'writing' && test.testType === 'mock') {
      const score = test.teacher_score !== null ? test.teacher_score : test.score;
      if (score === null || score === undefined) return 'text-gray-600';
      if (score >= 8) return 'text-green-600';
      if (score >= 6) return 'text-yellow-600';
      return 'text-red-600';
    }

    // For MCQ tests
    if (test.scorePercentage >= 70) return 'text-green-600';
    if (test.scorePercentage >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Get progress bar color based on test type and score
  const getProgressBarColor = (test) => {
    if (test.subject === 'writing' && test.testType === 'mock') {
      const score = test.teacher_score !== null ? test.teacher_score : test.score;
      if (score === null || score === undefined) return '#6B7280'; // gray
      if (score >= 8) return '#10B981'; // green
      if (score >= 6) return '#F59E0B'; // yellow
      return '#EF4444'; // red
    }

    // For MCQ tests
    if (test.scorePercentage >= 70) return '#10B981';
    if (test.scorePercentage >= 40) return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  const formatSubjectName = (subject) => {
    if (!subject) return '';

    const subjectMap = {
      'maths': 'Maths',
      'thinkingskills': 'Thinking Skills',
      'writing': 'Writing',
      'reading': 'Reading',
      'science': 'Science',
      'english': 'English'
    };

    // First try exact match
    if (subjectMap[subject.toLowerCase()]) {
      return subjectMap[subject.toLowerCase()];
    }

    // Handle camelCase or variations
    const formattedSubject = subject.toLowerCase();
    if (formattedSubject.includes('thinking')) {
      return 'Thinking Skills Test';
    }
    if (formattedSubject.includes('writing')) {
      return 'Writing Test';
    }
    if (formattedSubject.includes('reading')) {
      return 'Reading Test';
    }
    if (formattedSubject.includes('math')) {
      return 'Mathematical Reasoning Test';
    }

    // Default: capitalize first letter
    return subject.charAt(0).toUpperCase() + subject.slice(1);
  };

  return (
    <div className="min-h-screen bg-teal-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate("/new-student-dashboard")}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <FaChevronLeft className="mr-1" /> Back
          </button>
          <h1 className="text-3xl font-bold text-gray-800 mt-4 flex items-center">
            <FaHistory className="mr-2 text-blue-500" />
            {filterType === 'premium' ? 'Premium Tests History' :
              filterType === 'subjects' ? 'Subject Tests History' :
                'Mock Tests History'}
          </h1>
        </div>

        {filteredHistory.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-600">
              {filterType === 'all' ? "You haven't taken any tests yet." :
                filterType === 'premium' ? "No premium tests found in your history." :
                  "No subject tests found in your history."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-500 uppercase tracking-wider">Test</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-500 uppercase tracking-wider">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-purple-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredHistory.map((test, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {/* {test.subject === 'writing' && (
                          <FaPenAlt className="mr-2 text-blue-500" />
                        )} */}
                        <div className="font-medium text-gray-900">
                          {getTestTypeName(test.testType, test.category, test.subject)}
                          {/* {test.subject === 'writing' && test.testType === 'mock' && (
                            <span className="ml-2 text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                              Writing
                            </span>
                          )} */}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {formatTestDate(test.completedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {/* Show circular progress bar for MCQ tests, simple score for writing */}
                        {test.subject === 'writing' && test.testType === 'mock' ? (
                          <div className="flex items-center">
                            <span className={`font-bold text-lg ${getScoreColor(test)}`}>
                              {getScoreDisplay(test)}
                            </span>
                            {test.teacher_score !== null && test.teacher_score !== undefined && (
                              <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                Teacher Graded
                              </span>
                            )}
                            {test.teacher_score === null && test.score !== null && (
                              <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                AI Graded
                              </span>
                            )}
                            {test.teacher_score === null && test.score === null && (
                              <span className="ml-2 text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full">
                                Pending
                              </span>
                            )}
                          </div>
                        ) : (
                          // MCQ Test Score Display
                          <div className="flex items-center">
                            <div style={{ width: 40, height: 40, marginRight: 10 }}>
                              <CircularProgressbar
                                value={test.scorePercentage}
                                text=""
                                styles={buildStyles({
                                  pathColor: getProgressBarColor(test),
                                  trailColor: '#E5E7EB',
                                })}
                              />
                            </div>
                            <span className={`font-medium ${getScoreColor(test)}`}>
                              {getScoreDisplay(test)}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => navigate(`/test-results/${test.testType}/${test.testId}`)}
                        className="text-yellow-600 hover:text-yellow-900 flex items-center"
                      >
                        View <FaChevronRight className="ml-1" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestHistoryPage;